import { DeferredMedia } from '@theme/deferred-media'
import { MediaStartedPlayingEvent } from '@theme/events'

class VideoMedia extends DeferredMedia {
  #attachedVideoListeners = new WeakSet()
  #customControlsAbortController = null

  constructor() {
    super()
    this._manualControl = false
    this._setupHoverPlay()
  }

  get hoverPlay() {
    return this.hasAttribute('hover-play')
  }

  connectedCallback() {
    if (this.hoverPlay && !this.autoplay) {
      this.paused = true
    }

    super.connectedCallback()
    this._setupCustomControls()

    if (this.hoverPlay && !this.autoplay) {
      this._waitForContentAndInit()
    }

    this._updatePlayPauseIcons()
    this._updateMuteIcons(this.querySelector('video')?.muted ?? false)
  }

  disconnectedCallback() {
    this.#customControlsAbortController?.abort()
    super.disconnectedCallback()
  }

  get isLocalDevHost() {
    const hostname = globalThis.location?.hostname
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '0.0.0.0'
    )
  }

  get localStoreOrigin() {
    if (!this.isLocalDevHost) {
      return null
    }

    if (globalThis.Shopify?.shop) {
      return `https://${globalThis.Shopify.shop}`
    }

    const featuresElement = document.getElementById('shopify-features')
    if (!featuresElement?.textContent) {
      return null
    }

    try {
      const features = JSON.parse(featuresElement.textContent)
      if (!features?.domain) {
        return null
      }
      return `https://${features.domain}`
    } catch {
      return null
    }
  }

  normalizeLocalVideoUrl(url) {
    if (!url) {
      return url
    }

    const storeOrigin = this.localStoreOrigin
    if (!storeOrigin) {
      return url
    }

    if (url.startsWith('/cdn/')) {
      return `${storeOrigin}${url}`
    }

    if (url.startsWith('//cdn/')) {
      return `${storeOrigin}/cdn/${url.slice(6)}`
    }

    return url
  }

  normalizeLocalVideoNode(videoNode) {
    if (!(videoNode instanceof HTMLElement)) {
      return
    }

    if (videoNode.tagName !== 'VIDEO') {
      return
    }

    const sourceUrl = videoNode.getAttribute('src')
    if (sourceUrl) {
      videoNode.setAttribute('src', this.normalizeLocalVideoUrl(sourceUrl))
    }

    const posterUrl = videoNode.getAttribute('poster')
    if (posterUrl) {
      videoNode.setAttribute('poster', this.normalizeLocalVideoUrl(posterUrl))
    }

    for (const source of videoNode.querySelectorAll('source')) {
      const sourceSrc = source.getAttribute('src')
      if (sourceSrc) {
        source.setAttribute('src', this.normalizeLocalVideoUrl(sourceSrc))
      }
    }
  }

  _setupHoverPlay() {
    if (!this.hoverPlay) return

    this.addEventListener('mouseenter', () => {
      if (!this._manualControl) {
        this.paused = false
        this.play()
      }
    })

    this.addEventListener('mouseleave', () => {
      if (!this._manualControl) {
        this.paused = true
        this.pause()
      }
    })
  }

  _setupCustomControls() {
    this.#customControlsAbortController?.abort()
    this.#customControlsAbortController = new AbortController()
    this.addEventListener('click', this.#onCustomControlClick, {
      signal: this.#customControlsAbortController.signal
    })
  }

  #onCustomControlClick = (event) => {
    const control =
      event.target instanceof Element
        ? event.target.closest('[data-action]')
        : null
    if (!control || control.closest('video-media') !== this) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    if (control.dataset.action === 'play-pause') {
      this._togglePlayPause()
      return
    }

    if (control.dataset.action === 'toggle-mute') {
      this._toggleMute()
    }
  }

  _togglePlayPause() {
    this._manualControl = true

    if (this.playing) {
      this.paused = true
      this.pause()
      this._manualControl = false
    } else {
      this.paused = false
      this.play()
    }
    this._updatePlayPauseIcons()
  }

  _toggleMute() {
    const video = this.querySelector('video')
    if (video) {
      video.muted = !video.muted
      this._updateMuteIcons(video.muted)
    }
  }

  _updatePlayPauseIcons() {
    this.querySelector('[data-action="play-pause"]')?.classList.toggle(
      'is-secondary-icon',
      this.playing
    )
  }

  _updateMuteIcons(isMuted) {
    this.querySelector('[data-action="toggle-mute"]')?.classList.toggle(
      'is-secondary-icon',
      isMuted
    )
  }

  playerTarget() {
    const existingPlayer = this.querySelector('video, iframe[data-video-type]')
    if (existingPlayer) {
      this._attachVideoListeners(existingPlayer)
      this.markMediaLoaded()
      return existingPlayer
    }

    const templateElement = this.querySelector('template')
    if (templateElement) {
      const playerNode =
        templateElement.content.firstElementChild?.cloneNode(true)
      if (playerNode) {
        this.normalizeLocalVideoNode(playerNode)
        this.appendChild(playerNode)
      }
    } else {
      const player = this.querySelector('video')
      if (!player) {
        return {
          mute: () => {
            /* noop */
          },
          pause: () => {
            /* noop */
          },
          play: () => {
            /* noop */
          }
        }
      }
    }

    this.markMediaLoaded()

    const player = this.querySelector('video, iframe[data-video-type]')
    if (player) {
      this._attachVideoListeners(player)
    }
    return player
  }

  playerHandler(target, prop) {
    if (target instanceof HTMLIFrameElement) {
      this.postIframeMediaCommand(target, prop)
      return
    }

    if (prop === 'play') {
      const playPromise = target.play()
      if (playPromise instanceof Promise) {
        playPromise.catch((error) => {
          if (error.name === 'NotAllowedError') {
            this.setAttribute('suspended', '')
            target.controls = true
            const replacementImageSrc =
              target.previousElementSibling?.currentSrc
            if (replacementImageSrc) {
              target.poster = replacementImageSrc
            }
          }
        })
      }
    } else if (typeof target[prop] === 'function') {
      target[prop]()
    }
  }

  postIframeMediaCommand(target, prop) {
    if (prop === 'play') {
      this.dispatchEvent(new MediaStartedPlayingEvent(this))
      this.setAttribute('playing', '')
      this.updatePlayPauseHint(true)
      target.contentWindow?.postMessage(
        target.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      )
      return
    }

    if (prop === 'pause') {
      this.removeAttribute('playing')
      this.updatePlayPauseHint(false)
      target.contentWindow?.postMessage(
        target.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"pauseVideo","args":""}'
          : '{"method":"pause"}',
        '*'
      )
    }
  }

  _attachVideoListeners(player) {
    if (!(player instanceof HTMLVideoElement)) {
      return player
    }

    if (this.#attachedVideoListeners.has(player)) {
      return player
    }
    this.#attachedVideoListeners.add(player)

    if (this.autoplay && player.loop) {
      player.muted = true
    }

    player.addEventListener('play', () => {
      const suppressUnexpectedAutoplay =
        this.hoverPlay && !this._videoStarted && this.paused

      if (suppressUnexpectedAutoplay) {
        this._videoStarted = true
        player.pause()
        return
      }

      this._videoStarted = true
      this.dispatchEvent(new MediaStartedPlayingEvent(this))
      this.setAttribute('playing', '')
      this.removeAttribute('suspended')
      this._updatePlayPauseIcons()
      this.updatePlayPauseHint(true)
    })
    player.addEventListener('pause', () => {
      if (!player.seeking && player.paused) {
        this.removeAttribute('playing')
        this._updatePlayPauseIcons()
        this.updatePlayPauseHint(false)
      }
    })
    player.addEventListener('volumechange', () => {
      this._updateMuteIcons(player.muted)
    })
    return player
  }
}

class AnimatePicture extends HTMLPictureElement {
  connectedCallback() {
    const theme = globalThis.theme
    if (theme?.config?.motionReduced) {
      return
    }
    if (!theme?.Animation) {
      return
    }

    this.animation = new theme.Animation(this)
    this.animation.beforeLoad()

    if ('IntersectionObserver' in globalThis) {
      const observer = new IntersectionObserver(
        async (entries, obs) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue
            if (!this.immediate && this.media) {
              await theme.utils.imageLoaded(this.media)
            }
            this.animation.load()
            obs.unobserve(this)
          }
        },
        {
          root: null,
          rootMargin: '0px 0px 0px 0px',
          threshold: 0.1
        }
      )
      observer.observe(this)
    } else {
      this.animation.load()
    }
  }

  get media() {
    return this.querySelector('img, source')
  }

  get immediate() {
    return this.hasAttribute('data-immediate')
  }
}

if (!customElements.get('video-media')) {
  customElements.define('video-media', VideoMedia)
}
if (!customElements.get('deferred-media')) {
  customElements.define(
    'deferred-media',
    class DeferredMediaElement extends VideoMedia {}
  )
}
if (!customElements.get('animate-picture')) {
  customElements.define('animate-picture', AnimatePicture, {
    extends: 'picture'
  })
}
